import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Typography, IconButton as MuiIconButton } from '@material-ui/core'
import { ArrowUp3, ArrowDown } from '@refera/ui-icons'
import clsx from 'clsx'
import useStyles from './style'
import Theme from '@refera/ui-core'

/**
 * Filter label component with sort icon.
 *
 * Props:
 * - label: string (label text)
 * - sort: 'asc' | 'desc' | null (sort state, optional, controlled mode)
 * - onClick: function called when clicking the label or icon (receives new state)
 * - className: string (optional, extra classes)
 */
const FilterLabel = ({ label, sort: sortProp, onClick, className }) => {
  const styles = useStyles()
  const [internalSort, setInternalSort] = useState(null)
  const [hover, setHover] = useState(false)

  // Decide se usa o estado controlado ou interno
  const sort = sortProp !== undefined ? sortProp : internalSort

  const handleToggleSort = useCallback(() => {
    let nextSort
    if (sort === null) nextSort = 'asc'
    else if (sort === 'asc') nextSort = 'desc'
    else nextSort = null

    setInternalSort(nextSort)
    if (onClick) onClick(nextSort)
  }, [sort, onClick])

  // O botão sempre é renderizado, mas fica opaco se sort === null e não está em hover/focus
  const iconOpacity = sort !== null || hover ? 1 : 0

  return (
    <span
      className={clsx(styles.root, className)}
      onClick={handleToggleSort}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleToggleSort()}
      tabIndex={0}
      role="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      <Typography>{label}</Typography>
      <MuiIconButton
        size="small"
        onClick={e => {
          e.stopPropagation()
          handleToggleSort()
        }}
        className={styles.sortIconButton}
        style={{ opacity: iconOpacity, transition: 'opacity 0.1s' }}
        tabIndex={-1}
      >
        {sort === 'asc' && <ArrowUp3 color={Theme.Colors.Grayscale.SixtyFour} />}
        {sort === 'desc' && <ArrowDown color={Theme.Colors.Grayscale.SixtyFour} />}
        {sort === null && (
          <ArrowUp3
            className={styles.arrowIconUnselected}
            color={Theme.Colors.Grayscale.ThirtyTwo}
          />
        )}
      </MuiIconButton>
    </span>
  )
}

FilterLabel.propTypes = {
  label: PropTypes.string.isRequired,
  sort: PropTypes.oneOf(['asc', 'desc', null]),
  onClick: PropTypes.func,
  className: PropTypes.string,
}

FilterLabel.defaultProps = {
  sort: undefined,
  onClick: undefined,
  className: undefined,
}

export default FilterLabel
